'use client';

import { type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/libs';

import { IconDynamic, type IconName } from '../Icon';

export type AlertVariant = 'error' | 'warning' | 'info' | 'success';

export type AlertProps = HTMLAttributes<HTMLDivElement> & {
  variant?: AlertVariant;
  title?: string;
  description?: string;
  icon?: IconName;
  children?: ReactNode;
};

const variantStyles: Record<
  AlertVariant,
  { container: string; icon: string; title: string; description: string }
> = {
  error: {
    container: 'bg-red-50 border-2 border-red-300',
    icon: 'text-red-600',
    title: 'text-red-700',
    description: 'text-red-600',
  },
  warning: {
    container: 'bg-yellow-50 border-2 border-yellow-300',
    icon: 'text-yellow-600',
    title: 'text-yellow-700',
    description: 'text-yellow-600',
  },
  info: {
    container: 'bg-blue-50 border-2 border-blue-300',
    icon: 'text-blue-600',
    title: 'text-blue-700',
    description: 'text-blue-600',
  },
  success: {
    container: 'bg-green-50 border-2 border-green-300',
    icon: 'text-green-600',
    title: 'text-green-700',
    description: 'text-green-600',
  },
};

const defaultIcons: Record<AlertVariant, IconName> = {
  error: 'Info',
  warning: 'Info',
  info: 'Info',
  success: 'Check',
};

export const Alert = ({
  variant = 'info',
  title,
  description,
  icon,
  children,
  className,
  ...props
}: AlertProps) => {
  const styles = variantStyles[variant];
  const iconName = icon || defaultIcons[variant];

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-3 rounded-lg text-left',
        styles.container,
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-2">
        {iconName && (
          <div className={cn('flex-shrink-0 mt-0.5', styles.icon)}>
            {IconDynamic(iconName, {
              className: 'w-5 h-5',
              width: 20,
              height: 20,
            })}
          </div>
        )}
        <div className="flex flex-col gap-1 flex-1 text-left">
          {title && <p className={cn('text-sm font-semibold', styles.title)}>{title}</p>}
          {description && (
            <p className={cn('text-sm leading-relaxed', styles.description)}>
              {description}
            </p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
